import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Checkbox,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: '#f5f7fa',
}));

const StyledTableCell = styled(TableCell)(({ theme, isHeader }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: isHeader ? '0.75rem' : '0.8125rem',
    padding: isHeader ? '12px 12px' : '10px 12px',

    ...(isHeader && {
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#455a64',
        backgroundColor: '#f5f7fa',
        borderBottom: `2px solid ${theme.palette.divider}`,
    }),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: '#f5f7fa',
    },
    '&.Mui-selected': {
        backgroundColor: '#e3f2fd',
        '&:hover': {
            backgroundColor: '#bbdefb',
        },
    },
    '&:focus-within': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
    },
}));

const LsTable = ({
    columns = [],
    rows = [],
    selectable = false,
    selected = [],
    onSelectRow,
    onSelectAll,
    dense = true,
    pagination = false,
    page = 0,
    rowsPerPage = 10,
    totalRows = 0,
    onPageChange,
    onRowsPerPageChange,
    actions,
    ...props
}) => {
    const isSelected = (id) => selected.indexOf(id) !== -1;

    return (
        <>
            <StyledTableContainer component={Paper}>
                <Table size={dense ? 'small' : 'medium'} {...props}>
                    <StyledTableHead>
                        <TableRow>
                            {selectable && (
                                <StyledTableCell padding="checkbox" isHeader>
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < rows.length}
                                        checked={rows.length > 0 && selected.length === rows.length}
                                        onChange={onSelectAll}
                                        size="small"
                                    />
                                </StyledTableCell>
                            )}
                            {columns.map((column) => (
                                <StyledTableCell
                                    key={column.field}
                                    align={column.align || 'left'}
                                    isHeader
                                    width={column.width}
                                >
                                    {column.headerName}
                                </StyledTableCell>
                            ))}
                            {actions && (
                                <StyledTableCell align="center" isHeader width={100}>
                                    Actions
                                </StyledTableCell>
                            )}
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const isItemSelected = selectable ? isSelected(row.id) : false;

                            return (
                                <StyledTableRow
                                    key={row.id}
                                    hover
                                    selected={isItemSelected}
                                    onClick={(e) => selectable && onSelectRow(e, row.id)}
                                    role={selectable ? 'checkbox' : undefined}
                                    aria-checked={isItemSelected}
                                    tabIndex={0}
                                >
                                    {selectable && (
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox checked={isItemSelected} size="small" />
                                        </StyledTableCell>
                                    )}
                                    {columns.map((column) => (
                                        <StyledTableCell
                                            key={column.field}
                                            align={column.align || 'left'}
                                        >
                                            {column.renderCell
                                                ? column.renderCell(row)
                                                : row[column.field]}
                                        </StyledTableCell>
                                    ))}
                                    {actions && (
                                        <StyledTableCell align="center">
                                            {actions(row)}
                                        </StyledTableCell>
                                    )}
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            {pagination && (
                <TablePagination
                    component="div"
                    count={totalRows}
                    page={page}
                    onPageChange={onPageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            )}
        </>
    );
};

export default LsTable;